import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft } from "lucide-react";
import Logo from "@/components/Logo";

const GOALS = [
  { id: "reset", title: "Reset & Restore", desc: "Come back to your body after a long stretch of neglect." },
  { id: "energy", title: "Sustained Energy", desc: "Feel steady, alert, and ready without collapsing at 3pm." },
  { id: "strength", title: "Strength & Shape", desc: "Feel powerful. Watch your body shift honestly." },
  { id: "peace", title: "Inner Peace", desc: "Quieter mornings. Kinder self-talk. Boundaries that hold." },
];
const DIETARY = ["Vegetarian", "Vegan", "Pescatarian", "Gluten-free", "Dairy-free", "Nut allergy", "No restrictions"];
const LEVELS = [
  { id: "new", label: "New to movement" },
  { id: "casual", label: "Casual mover" },
  { id: "regular", label: "Regularly active" },
  { id: "advanced", label: "Very active" },
];
const FOCUS = ["Sleep", "Stress", "Body composition", "Confidence", "Consistency", "Joy"];

export default function Onboarding() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [goal, setGoal] = useState("");
  const [dietary, setDietary] = useState([]);
  const [level, setLevel] = useState("");
  const [focus, setFocus] = useState([]);
  const [obstacles, setObstacles] = useState("");

  const toggle = (list, setList, v) => {
    setList(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  };

  const steps = [
    { title: "What are you here for?", key: "goal", ready: !!goal },
    { title: "How do you eat?", key: "dietary", ready: true },
    { title: "How's your body moving now?", key: "level", ready: !!level },
    { title: "What do you want to grow?", key: "focus", ready: focus.length > 0 },
    { title: "What has stopped you before?", key: "obstacles", ready: true },
  ];

  const finish = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/onboarding", {
        goal,
        dietary,
        fitness_level: level,
        focus_areas: focus,
        obstacles,
      });
      setUser(data);
      toast.success("Your practice is ready.");
      navigate("/app");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const current = steps[step];

  return (
    <div className="min-h-screen p-6 md:p-16 max-w-3xl mx-auto flex flex-col">
      <div className="flex items-center justify-between mb-16">
        <Logo size={80} data-testid="onboarding-logo" />
        <div className="flex gap-2" data-testid="onboarding-progress">
          {steps.map((_, i) => (
            <div key={i} className={`h-[2px] w-8 ${i <= step ? "bg-[hsl(var(--primary))]" : "bg-border"}`} />
          ))}
        </div>
      </div>

      <div className="flex-1 fade-in-up" key={step}>
        <div className="label-eyebrow mb-6">Step {step + 1} of {steps.length}</div>
        <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] mb-12">{current.title}</h1>

        {step === 0 && (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="step-goal">
            {GOALS.map((g) => (
              <button
                key={g.id}
                onClick={() => setGoal(g.id)}
                data-testid={`goal-${g.id}`}
                className={`text-left p-6 border transition-colors ${
                  goal === g.id
                    ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5"
                    : "border-border hover:border-stone-400"
                }`}
              >
                <div className="font-serif text-2xl mb-2">{g.title}</div>
                <div className="text-sm text-stone-600 leading-relaxed">{g.desc}</div>
              </button>
            ))}
          </div>
        )}

        {step === 1 && (
          <div className="flex flex-wrap gap-3" data-testid="step-dietary">
            {DIETARY.map((d) => (
              <button
                key={d}
                onClick={() => toggle(dietary, setDietary, d)}
                data-testid={`dietary-${d.toLowerCase().replace(/\s/g, "-")}`}
                className={`px-5 py-3 border text-sm transition-colors ${
                  dietary.includes(d)
                    ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-white"
                    : "border-border hover:border-stone-400"
                }`}
              >
                {d}
              </button>
            ))}
          </div>
        )}

        {step === 2 && (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="step-level">
            {LEVELS.map((l) => (
              <button
                key={l.id}
                onClick={() => setLevel(l.id)}
                data-testid={`level-${l.id}`}
                className={`p-6 border text-left transition-colors ${
                  level === l.id
                    ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5"
                    : "border-border hover:border-stone-400"
                }`}
              >
                <div className="font-serif text-2xl">{l.label}</div>
              </button>
            ))}
          </div>
        )}

        {step === 3 && (
          <div className="flex flex-wrap gap-3" data-testid="step-focus">
            {FOCUS.map((f) => (
              <button
                key={f}
                onClick={() => toggle(focus, setFocus, f)}
                data-testid={`focus-${f.toLowerCase().replace(/\s/g, "-")}`}
                className={`px-5 py-3 border text-sm transition-colors ${
                  focus.includes(f)
                    ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-white"
                    : "border-border hover:border-stone-400"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        )}

        {step === 4 && (
          <textarea
            value={obstacles}
            onChange={(e) => setObstacles(e.target.value)}
            data-testid="obstacles-input"
            rows={6}
            placeholder="Time. Perfectionism. Grief. Whatever is true. This is private."
            className="w-full bg-transparent border border-border p-5 no-shadow-focus text-base leading-relaxed"
          />
        )}
      </div>

      <div className="flex items-center justify-between mt-16 border-t border-border pt-8">
        <button
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
          data-testid="onboarding-back"
          className="flex items-center gap-2 text-sm text-stone-500 hover:text-[hsl(var(--foreground))] disabled:opacity-30"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back
        </button>
        {step < steps.length - 1 ? (
          <button
            onClick={() => setStep((s) => s + 1)}
            disabled={!current.ready}
            data-testid="onboarding-next"
            className="flex items-center gap-2 bg-[hsl(var(--foreground))] text-white px-6 py-3 hover:bg-[hsl(var(--primary))] transition-colors disabled:opacity-40"
          >
            Continue <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
        ) : (
          <button
            onClick={finish}
            disabled={saving}
            data-testid="onboarding-finish"
            className="flex items-center gap-2 bg-[hsl(var(--primary))] text-white px-6 py-3 hover:bg-[hsl(var(--foreground))] transition-colors disabled:opacity-60"
          >
            {saving ? "Saving…" : "Begin"}<ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
        )}
      </div>
    </div>
  );
}
